import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {Provider} from 'react-redux';
import RedBox from 'redbox-react';
import {SwitcherProvider} from 'switcheroo';
import {HashRouter, Route, Routes} from 'react-router-dom';
import AppHooksProvider from '~/components/AppHooksProvider';
import AnalyticsProvider from '~/components/AnalyticsProvider';
// actions
import {mounted} from '~/actions/AppActions';
// components
import FeatureFlags from '~/shared/components/FeatureFlags';
import {SignalProvider} from 'signalerjs-react';
// constants
import features from '~/constants/FeatureFlags';
import LocationInfo from '~/components/LocationInfo';
// vendor
import rollbar from '~/vendor/rollbar';
import {AuthProvider} from '~/auth/AuthProvider';
import {__DEVELOPMENT__} from '~/utils/env';
import {AnalyticsTracking} from '~/components/Analytics/AnalyticsTracking';
import {AuthSuccessTracker} from '~/auth/ci-header/AuthHeaderLoginListener';

const propTypes = {
  store: PropTypes.object.isRequired
};

const client = new QueryClient({
  defaultOptions: {
    queries: {refetchOnWindowFocus: false, networkMode: 'offlineFirst'},
    mutations: {networkMode: 'offlineFirst'}
  }
});

const ReactQueryProviders = ({children}) => (
  <QueryClientProvider client={client}>
    <ReactQueryDevtools initialIsOpen={false} />
    {children}
  </QueryClientProvider>
);

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
class Ndx extends Component {
  static displayName = 'Ndx';
  static propTypes = propTypes;

  state = {
    error: null
  };

  componentDidMount() {
    this.props.store.dispatch(mounted());
  }

  componentDidCatch(err) {
    if (__DEVELOPMENT__) {
      this.setState({error: err});
    } else {
      rollbar.error(err, {
        errorBoundaryComponent: 'Ndx'
      });
    }
  }

  renderError() {
    return <RedBox error={this.state.error} />;
  }

  renderApp() {
    const app = (
      <SwitcherProvider>
        <Provider key="provider" store={this.props.store}>
          <ReactQueryProviders>
            <LocationInfo>
              <AuthProvider>
                <AnalyticsProvider>
                  <AnalyticsTracking>
                    <AuthSuccessTracker>
                      <AppHooksProvider />
                    </AuthSuccessTracker>
                  </AnalyticsTracking>
                </AnalyticsProvider>
              </AuthProvider>
            </LocationInfo>
          </ReactQueryProviders>
        </Provider>
      </SwitcherProvider>
    );

    return (
      <SignalProvider features={features}>
        {signals =>
          signals && (
            <HashRouter>
              <Routes>
                <Route path="/signals" element={<FeatureFlags />} />
                <Route path="/*" element={app} />
              </Routes>
            </HashRouter>
          )
        }
      </SignalProvider>
    );
  }

  render() {
    const showError = __DEVELOPMENT__ && this.state.error;
    return showError ? this.renderError() : this.renderApp();
  }
}

export default Ndx;
